import { Container, LeftMenuWrapper, ResetButton, StickyBar } from './styles';
import {
  Category,
  Equipment,
  OfferType,
  OfferTypeObject,
  RangesDimensions,
  SearchTabs,
  UnitsMultilevelList,
  FiltersType,
} from '@/types';
import { getClampToMax, isOnlyLand } from '@/common';
import {
  LandMultilevelCheckboxList,
  CheckboxGroupMenu,
  Range,
  ClearFilterIcon,
  FilterResetIcon,
  MapPreviewIcon,
  LocationAutocomplete,
} from '@/components';
import { Box, Skeleton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { ReferenceLocation } from '@/types';
import { useAppContext } from '@/contexts';
import { CATEGORY_ORDER, COLORS, EQUIPS_ORDER, MAX_FILTERS_RANGE_VALUES } from '@/constants';
import { useTranslations } from 'next-intl';
import { SearchProfile } from '../SearchProfile';
import dynamic from 'next/dynamic';

const FilterMapPreview = dynamic(
  () =>
    import('@/components/shared/FilterMapPreview').then((m) => ({ default: m.FilterMapPreview })),
  { ssr: false },
);

export interface LeftMenuProps {
  categories: Array<Category>;
  categoriesValue: Array<Category>;
  setCategoriesValue: (categories: Array<Category>) => void;
  equipment: Array<Equipment>;
  equipmentValue: Array<Equipment>;
  setEquipmentValue: (categories: Array<Equipment>) => void;
  unitListValue: Array<string>;
  setUnitListValue: (value: string[]) => void;
  rangesDimensions: RangesDimensions;
  roomNumberValue: number[];
  setRoomNumberValue: (value: number[]) => void;
  priceValue: number[];
  setPriceValue: (value: number[]) => void;
  rentPriceValue: number[];
  setRentPriceValue: (value: number[]) => void;
  areaValue: number[];
  setAreaValue: (value: number[]) => void;
  powerConsumptionClassesList: Category[];
  powerConsumptionClasses: string[];
  setPowerConsumptionClasses: (value: string[]) => void;
  energyClassFactorsList: Category[];
  energyClassFactors: string[];
  setEnergyClassFactors: (value: string[]) => void;
  unitList?: UnitsMultilevelList;
  offerTypeList: OfferTypeObject[];
  offerTypeValue: OfferType;
  setOfferTypeValue: (value: OfferTypeObject[]) => void;
  locations: ReferenceLocation[];
  onLocationsChange: (locations: ReferenceLocation[]) => void;
  onFullResetFilters: () => void;
  canReset?: boolean;
  onGoToMap: () => void;
  onClearMapFilters: () => void;
  filters: FiltersType;
  isLoading?: boolean;
  viewType: SearchTabs;
  isProject?: boolean;
}

export const LeftMenu = ({
  isLoading,
  categories,
  categoriesValue,
  setCategoriesValue,
  equipment,
  equipmentValue,
  setEquipmentValue,
  setUnitListValue,
  unitListValue,
  rangesDimensions,
  areaValue,
  setAreaValue,
  roomNumberValue,
  setRoomNumberValue,
  priceValue,
  setPriceValue,
  rentPriceValue,
  setRentPriceValue,
  powerConsumptionClassesList,
  powerConsumptionClasses,
  setPowerConsumptionClasses,
  energyClassFactorsList,
  energyClassFactors,
  setEnergyClassFactors,
  unitList,
  offerTypeList,
  offerTypeValue,
  setOfferTypeValue,
  locations,
  onLocationsChange,
  onFullResetFilters,
  canReset,
  onGoToMap,
  onClearMapFilters,
  filters,
  viewType,
  isProject,
}: LeftMenuProps) => {
  const t = useTranslations();
  const { setIsStickyBarVisible } = useAppContext();

  const [isSearchProfileVisible, setIsSearchProfileVisible] = useState(false);
  const checkIsSearchProfileVisible = (check: boolean) => setIsSearchProfileVisible(check);

  const isStickyBarActive = !isSearchProfileVisible && viewType === SearchTabs.LIST;

  useEffect(() => {
    setIsStickyBarVisible(isStickyBarActive);
    return () => setIsStickyBarVisible(false);
  }, [isStickyBarActive, setIsStickyBarVisible]);

  const chosenOfferType = offerTypeList.filter((e) => e.code === offerTypeValue);
  const hasMapSelection = !!(filters.mapArea || filters.admunits?.length);

  if (isLoading) {
    return (
      <LeftMenuWrapper $isMap={viewType === SearchTabs.MAP}>
        <Container $isMap={viewType === SearchTabs.MAP}>
          <Skeleton variant="rounded" width="100%" height={36} />
          <Skeleton variant="rounded" width="100%" height={36} />
          {[44, 180, 100, 80, 80, 80, 80, 80].map((h, i) => (
            <Skeleton key={i} variant="rounded" width="100%" height={h} />
          ))}
        </Container>
      </LeftMenuWrapper>
    );
  }

  return (
    <LeftMenuWrapper $isMap={viewType === SearchTabs.MAP}>
      <Container $isMap={viewType === SearchTabs.MAP}>
        <SearchProfile
          fullWidth={true}
          filters={filters}
          isLoading={isLoading}
          viewType={viewType}
          isProject={isProject}
          elementIsInViewport={checkIsSearchProfileVisible}
        />

        {viewType !== SearchTabs.MAP && hasMapSelection && (
          <Box
            sx={{
              width: '100%',
              border: `1px solid ${COLORS['neutral/300']}`,
              borderRadius: '6px',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                padding: '10px 12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${COLORS['neutral/300']}`,
              }}
            >
              <Typography fontWeight={700} fontSize={16} lineHeight="125%">
                {t('searchResult.seeOnMap')}
              </Typography>
              <Box
                onClick={onGoToMap}
                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              >
                <MapPreviewIcon />
              </Box>
            </Box>
            <Box
              onClick={onGoToMap}
              sx={{
                cursor: 'pointer',
                padding: '8px 12px 0',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <FilterMapPreview
                mapArea={filters.mapArea}
                admunitIds={filters.admunits?.map((a) => a.id)}
                height={120}
              />
            </Box>
            <Box
              sx={{
                padding: '8px 12px 10px',
                borderTop: `1px solid ${COLORS['neutral/300']}`,
                marginTop: '8px',
              }}
            >
              <Typography
                component="button"
                type="button"
                onClick={onClearMapFilters}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: 13,
                  fontWeight: 500,
                  color: COLORS['primary/600'],
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  width: '100%',
                  justifyContent: 'center',
                  '&:hover': { color: COLORS['primary/700'] },
                }}
              >
                <FilterResetIcon style={{ color: COLORS['primary/600'] }} />
                {t('searchResult.clearMapSelection')}
              </Typography>
            </Box>
          </Box>
        )}

        {viewType !== SearchTabs.MAP && !hasMapSelection && (
          <LocationAutocomplete
            value={locations}
            onChange={onLocationsChange}
            label={t('mainHero.filter.searchPlaceholder')}
            placeholder={t('searchResult.searchPlaceholder')}
          />
        )}

        <CheckboxGroupMenu
          label={t('searchResult.offerType')}
          key={t('searchResult.offerType')}
          items={offerTypeList}
          value={chosenOfferType}
          onChange={setOfferTypeValue}
        />
        <CheckboxGroupMenu
          label={t('searchResult.group')}
          key={t('searchResult.group')}
          items={categories}
          value={categoriesValue}
          onChange={setCategoriesValue}
          order={CATEGORY_ORDER}
        />
        {viewType !== SearchTabs.MAP && !hasMapSelection && (
          <LandMultilevelCheckboxList
            label={t('searchResult.countryState')}
            key={t('searchResult.countryState')}
            unitList={unitList}
            unitListValue={unitListValue}
            setUnitListValue={setUnitListValue}
          />
        )}
        <CheckboxGroupMenu
          label={t('searchResult.equipment')}
          key={t('searchResult.equipment')}
          items={equipment}
          value={equipmentValue}
          onChange={setEquipmentValue}
          order={EQUIPS_ORDER}
        />
        {rangesDimensions.area[0] !== undefined && rangesDimensions.area[1] !== undefined && (
          <Range
            isArea
            key={t('searchResult.area')}
            label={t('searchResult.area')}
            minValue={rangesDimensions.area[0] || 0}
            maxValue={rangesDimensions.area[1]}
            value={areaValue}
            setValue={setAreaValue}
            step={1}
          />
        )}

        {rangesDimensions.roomNumber[0] !== undefined &&
          rangesDimensions.roomNumber[1] !== undefined &&
          !isOnlyLand(categoriesValue) && (
            <Range
              key={t('searchResult.rooms')}
              label={t('searchResult.rooms')}
              minValue={rangesDimensions.roomNumber[0] || 0}
              maxValue={getClampToMax(
                rangesDimensions.roomNumber[1],
                MAX_FILTERS_RANGE_VALUES.ROOMS,
              )}
              value={roomNumberValue}
              setValue={setRoomNumberValue}
              step={1}
            />
          )}
        {rangesDimensions.price[0] !== undefined &&
          rangesDimensions.price[1] !== undefined &&
          offerTypeValue === OfferType.SELL && (
            <Range
              isPrice
              key={t('searchResult.purchasePrice')}
              label={t('searchResult.purchasePrice')}
              minValue={rangesDimensions.price[0] || 0}
              maxValue={getClampToMax(
                rangesDimensions.price[1],
                MAX_FILTERS_RANGE_VALUES.SELL_PRICE,
              )}
              value={priceValue}
              setValue={setPriceValue}
              step={1}
            />
          )}
        {rangesDimensions.rentPrice[0] !== undefined &&
          rangesDimensions.rentPrice[1] !== undefined &&
          offerTypeValue === OfferType.RENT && (
            <Range
              isPrice
              key={t('searchResult.purchasePrice')}
              label={t('searchResult.purchasePrice')}
              minValue={rangesDimensions.rentPrice[0] || 0}
              maxValue={getClampToMax(
                rangesDimensions.rentPrice[1],
                MAX_FILTERS_RANGE_VALUES.RENT_PRICE,
              )}
              value={rentPriceValue}
              setValue={setRentPriceValue}
              step={1}
            />
          )}
        {powerConsumptionClassesList.length > 0 && (
          <CheckboxGroupMenu
            label={t('searchResult.hwb_title')}
            key={t('searchResult.hwb_title')}
            items={powerConsumptionClassesList}
            value={powerConsumptionClassesList.filter((item) =>
              powerConsumptionClasses.includes(item.code),
            )}
            onChange={(selected) => setPowerConsumptionClasses(selected.map((item) => item.code))}
            defaultOpen={false}
            hint={t('searchResult.hints.hwb')}
          />
        )}
        {energyClassFactorsList.length > 0 && (
          <CheckboxGroupMenu
            label={t('searchResult.fgee_title')}
            key={t('searchResult.fgee_title')}
            items={energyClassFactorsList}
            value={energyClassFactorsList.filter((item) => energyClassFactors.includes(item.code))}
            onChange={(selected) => setEnergyClassFactors(selected.map((item) => item.code))}
            defaultOpen={false}
            hint={t('searchResult.hints.fgee')}
          />
        )}
        <ResetButton variant="form-button" onClick={onFullResetFilters} disabled={!canReset}>
          <ClearFilterIcon />
          <Typography noWrap fontWeight={600} fontSize={12}>
            {t('searchResult.clear')}
          </Typography>
        </ResetButton>
      </Container>
      {!isSearchProfileVisible && viewType === SearchTabs.LIST && (
        <StickyBar>
          <SearchProfile
            fullWidth={false}
            filters={filters}
            isLoading={isLoading}
            viewType={viewType}
            isProject={isProject}
          />
        </StickyBar>
      )}
    </LeftMenuWrapper>
  );
};
